import {
  Badge,
  Divider,
  Group,
  List,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { PendingPlanResponse } from "../features/queue/api";

// Рендер pending-плана декомпозиции (status=awaiting_plan_approval):
// подзадачи (title + description + acceptance), общий чек-лист эпика
// и сопроводительный approval_text. Показывается рядом с
// PlanApprovalBanner, чтобы человек видел, что именно одобряет.
//
// approval_text рендерится как pre-wrap текст (а не markdown-HTML):
// своего markdown-рендерера в проекте пока нет, а dangerouslySetInnerHTML
// на free-text от LLM — XSS-риск. Перенос строк сохраняем.
export function PendingPlanView({
  plan,
}: {
  readonly plan: PendingPlanResponse;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Title order={3}>{t("task.pendingPlanTitle")}</Title>
        {plan.approval_text !== "" ? (
          <Text style={{ whiteSpace: "pre-wrap" }}>{plan.approval_text}</Text>
        ) : null}

        <Title order={4}>{t("task.pendingPlanSubtasksTitle")}</Title>
        {plan.subtasks.length > 0 ? (
          <Stack gap="sm">
            {plan.subtasks.map((subtask, index) => (
              <Paper key={subtask.plan_key} withBorder p="sm" radius="sm">
                <Stack gap="xs">
                  <Group gap="xs">
                    <Badge variant="light">{String(index + 1)}</Badge>
                    <Text fw={600}>{subtask.title}</Text>
                  </Group>
                  {subtask.description !== "" ? (
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {subtask.description}
                    </Text>
                  ) : null}
                  {subtask.acceptance.length > 0 ? (
                    <List size="sm" withPadding>
                      {subtask.acceptance.map((line, lineIndex) => (
                        <List.Item key={lineIndex}>{line}</List.Item>
                      ))}
                    </List>
                  ) : null}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Text c="dimmed">{t("task.pendingPlanSubtasksEmpty")}</Text>
        )}

        {plan.checklist.length > 0 ? (
          <>
            <Divider />
            <Title order={4}>{t("task.pendingPlanChecklistTitle")}</Title>
            <List size="sm">
              {plan.checklist.map((line, index) => (
                <List.Item key={index}>{line}</List.Item>
              ))}
            </List>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}
